/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo } from 'react'
import { useTheme } from 'styled-components'
import {
  FaCrosshairs,
  FaFileExcel,
  FaFilter,
  FaGlobeAmericas,
  FaMapMarkerAlt,
  FaStore,
  FaSyncAlt,
  FaTh,
  FaExchangeAlt,
  FaCheck,
  FaSpinner,
  FaPlay,
} from 'react-icons/fa'
import { MdLayers } from 'react-icons/md'
import api from '../../services/axios'
import {
  BatalhaGridResponse,
  DimensaoGeoBatalha,
  NivelProdutoBatalha,
} from './types'
import { exportBatalhaNavalToExcel } from './exportExcel'
import {
  NavalGridControlsBar,
  NavalGridWrapper,
  NavalScorecardBar,
  NavalGridContainer,
} from './styles'

interface SkuNavalBattleGridProps {
  distributorId?: string | number | null
  periodLabel: string
  selectedDistributorName: string
  startDate?: string
  endDate?: string
  uf?: string
}

export const SkuNavalBattleGrid: React.FC<SkuNavalBattleGridProps> = ({
  distributorId,
  periodLabel,
  selectedDistributorName,
  startDate,
  endDate,
  uf,
}) => {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'

  // Eixos e Dimensões
  const [nivelProduto, setNivelProduto] = useState<NivelProdutoBatalha>('marca')
  const [dimensaoGeo, setDimensaoGeo] = useState<DimensaoGeoBatalha>('uf')
  const [eixosInvertidos, setEixosInvertidos] = useState(false)

  // Filtros em Cascata de Produto
  const [filtroMarca, setFiltroMarca] = useState<string>('')
  const [filtroCategoria, setFiltroCategoria] = useState<string>('')
  const [filtroSubcategoria, setFiltroSubcategoria] = useState<string>('')

  // Filtros de Geografia
  const [filtroUf, setFiltroUf] = useState<string>(uf || 'GO')
  const [cidadeBusca, setCidadeBusca] = useState<string>('')

  useEffect(() => {
    if (uf) {
      setFiltroUf(uf)
    }
  }, [uf])

  // Listas de Opções Dinâmicas
  const [marcasList, setMarcasList] = useState<string[]>([])
  const [categoriasList, setCategoriasList] = useState<string[]>([])
  const [subcategoriasList, setSubcategoriasList] = useState<string[]>([])
  const [ufsList, setUfsList] = useState<string[]>([])
  const [cidadesList, setCidadesList] = useState<string[]>([])

  // Dados da Grade
  const [gridData, setGridData] = useState<BatalhaGridResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [hasApplied, setHasApplied] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Tooltip Flutuante
  const [tooltipInfo, setTooltipInfo] = useState<{
    visible: boolean
    x: number
    y: number
    prodNome: string
    geoNome: string
    hasPositivacao: boolean
    pdvs: number
    faturamento: number
    caixas: number
    hitRateProd: number
    hitRateGeo: number
  } | null>(null)

  // 1. Carregar Marcas e UFs iniciais
  useEffect(() => {
    const carregarIniciais = async () => {
      try {
        const [resMarcas, resUfs] = await Promise.all([
          api.post('/sku/batalha-naval-filtros', { tipo: 'marcas' }),
          api.post('/sku/batalha-naval-filtros', { tipo: 'ufs', distId: distributorId }),
        ])
        if (resMarcas.data?.success && resMarcas.data.dados?.length) {
          setMarcasList(resMarcas.data.dados)
          if (!filtroMarca) setFiltroMarca(resMarcas.data.dados[0])
        }
        if (resUfs.data?.success && resUfs.data.dados?.length) {
          setUfsList(resUfs.data.dados)
          if (uf && resUfs.data.dados.includes(uf)) {
            setFiltroUf(uf)
          } else if (!resUfs.data.dados.includes(filtroUf)) {
            setFiltroUf(resUfs.data.dados[0])
          }
        }
      } catch (err) {
        console.error('Erro ao carregar marcas e UFs iniciais:', err)
      }
    }
    carregarIniciais()
  }, [distributorId, uf])

  // 2. Cascata: Carregar Categorias quando Marca mudar
  useEffect(() => {
    if (!filtroMarca) return
    const carregarCategorias = async () => {
      try {
        const res = await api.post('/sku/batalha-naval-filtros', {
          tipo: 'categorias',
          marca: filtroMarca,
        })
        if (res.data?.success && res.data.dados?.length) {
          setCategoriasList(res.data.dados)
          setFiltroCategoria(res.data.dados[0])
        } else {
          setCategoriasList([])
          setFiltroCategoria('')
        }
      } catch (err) {
        console.error('Erro ao carregar categorias da marca:', err)
      }
    }
    carregarCategorias()
  }, [filtroMarca])

  // 3. Cascata: Carregar Subcategorias quando Categoria mudar
  useEffect(() => {
    if (!filtroCategoria) return
    const carregarSubcategorias = async () => {
      try {
        const res = await api.post('/sku/batalha-naval-filtros', {
          tipo: 'subcategorias',
          marca: filtroMarca,
          categoria: filtroCategoria,
        })
        if (res.data?.success && res.data.dados?.length) {
          setSubcategoriasList(res.data.dados)
          setFiltroSubcategoria(res.data.dados[0])
        } else {
          setSubcategoriasList([])
          setFiltroSubcategoria('')
        }
      } catch (err) {
        console.error('Erro ao carregar subcategorias:', err)
      }
    }
    carregarSubcategorias()
  }, [filtroCategoria, filtroMarca])

  // 4. Carregar Cidades quando UF mudar (para dimensão cidade)
  useEffect(() => {
    if (dimensaoGeo !== 'cidade' || !filtroUf) return
    const carregarCidades = async () => {
      try {
        const res = await api.post('/sku/batalha-naval-filtros', {
          tipo: 'cidades',
          uf: filtroUf,
          distId: distributorId,
        })
        if (res.data?.success && res.data.dados?.length) {
          setCidadesList(res.data.dados)
        } else {
          setCidadesList([])
        }
      } catch (err) {
        console.error('Erro ao carregar cidades da UF:', err)
      }
    }
    carregarCidades()
  }, [dimensaoGeo, filtroUf, distributorId])

  // 5. Carregar Dados da Grade Batalha Naval
  const fetchGrid = async () => {
    if (!startDate || !endDate) return
    setLoading(true)
    try {
      const payload: any = {
        startDate,
        endDate,
        distId: distributorId,
        nivelProduto,
        dimensaoGeo,
      }

      if (nivelProduto === 'categoria') {
        payload.filtroMarca = filtroMarca
      } else if (nivelProduto === 'subcategoria') {
        payload.filtroMarca = filtroMarca
        payload.filtroCategoria = filtroCategoria
      } else if (nivelProduto === 'sku') {
        payload.filtroMarca = filtroMarca
        payload.filtroCategoria = filtroCategoria
        payload.filtroSubcategoria = filtroSubcategoria
      }

      if (dimensaoGeo === 'cidade' || dimensaoGeo === 'pdv') {
        payload.filtroUf = filtroUf
      }

      const res = await api.post('/sku/batalha-naval-grid', payload)
      if (res.data?.success) {
        setGridData(res.data)
      } else {
        setGridData(null)
      }
    } catch (err) {
      console.error('Erro ao buscar dados do grid de Batalha Naval:', err)
      setGridData(null)
    } finally {
      setLoading(false)
    }
  }

  // Executa apenas quando o usuário clica em Aplicar Filtros
  const handleApplyFilters = () => {
    setHasApplied(true)
    fetchGrid()
  }

  // Manipulação de Exportação Excel
  const handleExportExcel = () => {
    if (!gridData) return
    setExporting(true)
    try {
      exportBatalhaNavalToExcel(
        gridData,
        periodLabel,
        selectedDistributorName,
        eixosInvertidos
      )
    } catch (err) {
      console.error('Erro ao exportar planilha da Batalha Naval:', err)
    } finally {
      setExporting(false)
    }
  }

  // Definição de Linhas e Colunas conforme Inversão
  // Primeiras colunas e primeiras linhas SEMPRE com o maior % de hit!
  const colunas = useMemo(() => {
    if (!gridData) return []
    const rawCols = eixosInvertidos ? gridData.geografias : gridData.produtos

    return [...rawCols].sort((a, b) => {
      const statsA = eixosInvertidos ? gridData.statsGeografias[a.id] : gridData.statsProdutos[a.id]
      const statsB = eixosInvertidos ? gridData.statsGeografias[b.id] : gridData.statsProdutos[b.id]
      const hitA = statsA?.hitRate ?? 0
      const hitB = statsB?.hitRate ?? 0

      // 1. Maior % de hit primeiro!
      if (hitB !== hitA) {
        return hitB - hitA
      }
      // 2. Desempate: maior faturamento
      const fatA = statsA?.faturamento ?? 0
      const fatB = statsB?.faturamento ?? 0
      if (fatB !== fatA) {
        return fatB - fatA
      }
      return a.nome.localeCompare(b.nome)
    })
  }, [gridData, eixosInvertidos])

  const linhas = useMemo(() => {
    if (!gridData) return []
    const rawRows = eixosInvertidos ? gridData.produtos : gridData.geografias

    return [...rawRows].sort((a, b) => {
      const statsA = eixosInvertidos ? gridData.statsProdutos[a.id] : gridData.statsGeografias[a.id]
      const statsB = eixosInvertidos ? gridData.statsProdutos[b.id] : gridData.statsGeografias[b.id]
      const hitA = statsA?.hitRate ?? 0
      const hitB = statsB?.hitRate ?? 0

      if (hitB !== hitA) {
        return hitB - hitA
      }
      const fatA = statsA?.faturamento ?? 0
      const fatB = statsB?.faturamento ?? 0
      if (fatB !== fatA) {
        return fatB - fatA
      }
      return a.nome.localeCompare(b.nome)
    })
  }, [gridData, eixosInvertidos])

  const labelLinhas = useMemo(() => {
    if (!gridData) return ''
    return eixosInvertidos
      ? `Produto (${gridData.nivelProduto.toUpperCase()})`
      : `Geografia / Praça (${gridData.dimensaoGeo.toUpperCase()})`
  }, [gridData, eixosInvertidos])

  const labelColunas = useMemo(() => {
    if (!gridData) return ''
    return eixosInvertidos
      ? `Geografia (${gridData.dimensaoGeo.toUpperCase()})`
      : `Produto (${gridData.nivelProduto.toUpperCase()})`
  }, [gridData, eixosInvertidos])

  // Tooltip mouse handlers
  const handleMouseEnterCell = (
    e: React.MouseEvent,
    rowItem: any,
    colItem: any
  ) => {
    if (!gridData) return
    const gId = eixosInvertidos ? colItem.id : rowItem.id
    const pId = eixosInvertidos ? rowItem.id : colItem.id
    const key = `${gId}__${pId}`
    const cell = gridData.cells[key]
    const prodNome = eixosInvertidos ? rowItem.nome : colItem.nome
    const geoNome = eixosInvertidos ? colItem.nome : rowItem.nome

    const statsProd = gridData.statsProdutos[pId]
    const statsGeo = gridData.statsGeografias[gId]

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()

    setTooltipInfo({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      prodNome,
      geoNome,
      hasPositivacao: !!cell?.hasPositivacao,
      pdvs: cell?.pdvsPositivados || 0,
      faturamento: cell?.faturamento || 0,
      caixas: cell?.caixas || 0,
      hitRateProd: statsProd?.hitRate || 0,
      hitRateGeo: statsGeo?.hitRate || 0,
    })
  }

  const handleMouseLeaveCell = () => {
    setTooltipInfo(null)
  }

  return (
    <NavalGridWrapper>
      {/* 1. BARRA DE CONTROLES E FILTROS DINÂMICOS EM CASCATA */}
      <NavalGridControlsBar>
        {/* Linha 1: Eixo Produto e Cascata */}
        <div className='controls-row'>
          <div className='control-group'>
            <span className='group-label'>
              <MdLayers /> Eixo Produto:
            </span>
            <div className='pills-group'>
              <button
                type='button'
                className={nivelProduto === 'marca' ? 'active' : ''}
                onClick={() => setNivelProduto('marca')}
              >
                Marcas
              </button>
              <button
                type='button'
                className={nivelProduto === 'categoria' ? 'active' : ''}
                onClick={() => setNivelProduto('categoria')}
              >
                Categorias
              </button>
              <button
                type='button'
                className={nivelProduto === 'subcategoria' ? 'active' : ''}
                onClick={() => setNivelProduto('subcategoria')}
              >
                Subcategorias
              </button>
              <button
                type='button'
                className={nivelProduto === 'sku' ? 'active' : ''}
                onClick={() => setNivelProduto('sku')}
              >
                SKUs
              </button>
            </div>
          </div>

          {/* Filtros em Cascata do Produto */}
          <div className='control-group'>
            {nivelProduto !== 'marca' && (
              <select
                className='select-filter'
                value={filtroMarca}
                onChange={e => setFiltroMarca(e.target.value)}
                title='Marca obrigatória para afunilamento'
              >
                {marcasList.map(m => (
                  <option key={m} value={m}>
                    Marca: {m}
                  </option>
                ))}
              </select>
            )}

            {(nivelProduto === 'subcategoria' || nivelProduto === 'sku') && (
              <select
                className='select-filter'
                value={filtroCategoria}
                onChange={e => setFiltroCategoria(e.target.value)}
                title='Categoria obrigatória para afunilamento'
              >
                {categoriasList.map(c => (
                  <option key={c} value={c}>
                    Cat: {c}
                  </option>
                ))}
              </select>
            )}

            {nivelProduto === 'sku' && (
              <select
                className='select-filter'
                value={filtroSubcategoria}
                onChange={e => setFiltroSubcategoria(e.target.value)}
                title='Subcategoria obrigatória para listar SKUs'
              >
                {subcategoriasList.map(s => (
                  <option key={s} value={s}>
                    Subcat: {s}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Linha 2: Eixo Geográfico, Inversão e Ações */}
        <div className='controls-row'>
          <div className='control-group'>
            <span className='group-label'>
              <FaGlobeAmericas /> Geografia / Distribuição:
            </span>
            <div className='pills-group'>
              <button
                type='button'
                className={dimensaoGeo === 'uf' ? 'active' : ''}
                onClick={() => setDimensaoGeo('uf')}
              >
                UF
              </button>
              <button
                type='button'
                className={dimensaoGeo === 'cidade' ? 'active' : ''}
                onClick={() => setDimensaoGeo('cidade')}
              >
                Cidade
              </button>
              <button
                type='button'
                className={dimensaoGeo === 'distribuidor' ? 'active' : ''}
                onClick={() => setDimensaoGeo('distribuidor')}
              >
                Distribuidor
              </button>
              <button
                type='button'
                className={dimensaoGeo === 'pdv' ? 'active' : ''}
                onClick={() => setDimensaoGeo('pdv')}
              >
                PDV (Loja)
              </button>
            </div>

            {/* Sub-filtro para Cidade ou PDV: Selecionar UF */}
            {(dimensaoGeo === 'cidade' || dimensaoGeo === 'pdv') && (
              <select
                className='select-filter'
                value={filtroUf}
                onChange={e => setFiltroUf(e.target.value)}
                style={{ minWidth: 100 }}
                title='Filtrar estado (UF) das cidades/PDVs'
              >
                {ufsList.map(u => (
                  <option key={u} value={u}>
                    UF: {u}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Botões de Ação */}
          <div className='control-group'>
            <button
              type='button'
              className='btn-apply-filter'
              onClick={handleApplyFilters}
              disabled={loading}
              title='Aplicar filtros selecionados e gerar o tabuleiro de Batalha Naval'
            >
              {loading ? (
                <>
                  <FaSpinner className='spin' />
                  <span>Gerando...</span>
                </>
              ) : (
                <>
                  <FaPlay size={10} />
                  <span>Aplicar Filtros</span>
                </>
              )}
            </button>

            <button
              type='button'
              className='btn-swap-axis'
              onClick={() => setEixosInvertidos(!eixosInvertidos)}
              title='Alternar Linhas e Colunas do tabuleiro'
            >
              <FaExchangeAlt /> Inverter Eixos (X ⇄ Y)
            </button>

            <button
              type='button'
              className='btn-export-grid'
              onClick={handleExportExcel}
              disabled={exporting || !gridData}
              title='Baixar planilha XLSX da grade de Batalha Naval'
            >
              {exporting ? <FaSpinner className='spin' /> : <FaFileExcel />}
              Exportar Grade (.xlsx)
            </button>
          </div>
        </div>
      </NavalGridControlsBar>

      {/* 2. SCORECARD DE KPIs DA BATALHA NAVAL */}
      {gridData && (
        <NavalScorecardBar>
          <div className='scorecard-item'>
            <div className='icon-box emerald'>
              <FaCrosshairs />
            </div>
            <div className='meta'>
              <span className='label'>Taxa de Positivação (Hit Rate)</span>
              <span className='value'>{gridData.totais.hitRateGeral}%</span>
              <span className='sub'>
                {gridData.totais.totalPositivadas} acertos de {gridData.totais.totalCombinacoes}
              </span>
            </div>
          </div>

          <div className='scorecard-item'>
            <div className='icon-box blue'>
              <FaTh />
            </div>
            <div className='meta'>
              <span className='label'>Dimensões Ativas</span>
              <span className='value'>
                {linhas.length} × {colunas.length}
              </span>
              <span className='sub'>
                {labelLinhas} vs {labelColunas}
              </span>
            </div>
          </div>

          <div className='scorecard-item'>
            <div className='icon-box amber'>
              <FaStore />
            </div>
            <div className='meta'>
              <span className='label'>Faturamento da Grade</span>
              <span className='value'>
                {(gridData.totais.faturamentoTotal || 0).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  maximumFractionDigits: 0,
                })}
              </span>
              <span className='sub'>
                {(gridData.totais.caixasTotal || 0).toLocaleString('pt-BR', {
                  maximumFractionDigits: 0,
                })}{' '}
                caixas vendidas
              </span>
            </div>
          </div>
        </NavalScorecardBar>
      )}

      {/* 3. TABELA / GRADE DE BATALHA NAVAL */}
      <NavalGridContainer>
        {!hasApplied ? (
          <div style={{ textAlign: 'center', padding: '70px 20px', color: '#64748b' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: isLight ? 'rgba(0, 31, 92, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
                color: isLight ? '#001f5c' : '#93c5fd',
              }}
            >
              <FaCrosshairs size={32} />
            </div>
            <h3 style={{ color: isLight ? '#0f172a' : '#fff', margin: '0 0 8px 0', fontWeight: 700 }}>
              Pronto para Gerar a Batalha Naval
            </h3>
            <p
              style={{
                margin: '0 auto 20px auto',
                maxWidth: 480,
                fontSize: 13,
                lineHeight: 1.6,
                color: isLight ? '#475569' : '#94a3b8',
              }}
            >
              Configure o nível do produto e a dimensão geográfica acima. Em seguida, clique em <strong>Aplicar Filtros</strong> para gerar a grade de positivação com as colunas ordenadas por maior % de acerto.
            </p>
            <button
              type='button'
              className='btn-apply-filter'
              style={{ margin: '0 auto', padding: '10px 24px', fontSize: 13 }}
              onClick={handleApplyFilters}
              disabled={loading}
            >
              <FaPlay size={11} style={{ marginRight: 6 }} /> Aplicar Filtros e Gerar Tabuleiro
            </button>
          </div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <FaSpinner className='spin' size={32} color='#3b82f6' style={{ marginBottom: 12 }} />
            <h4 style={{ color: isLight ? '#0f172a' : '#fff', margin: '0 0 6px 0' }}>
              Carregando Tabuleiro de Batalha Naval...
            </h4>
            <p style={{ margin: 0, fontSize: 13 }}>
              Calculando mapa de positivação entre produtos e praças selecionadas.
            </p>
          </div>
        ) : !gridData || linhas.length === 0 || colunas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <FaCrosshairs size={40} color='#64748b' style={{ marginBottom: 12 }} />
            <h4 style={{ color: isLight ? '#0f172a' : '#fff', margin: '0 0 6px 0' }}>
              Nenhuma combinação encontrada para os filtros atuais
            </h4>
            <p style={{ margin: 0, fontSize: 13 }}>
              Experimente alterar os filtros de marca, categoria ou estado no topo.
            </p>
          </div>
        ) : (
          <div className='table-scroll'>
            <table>
              <thead>
                <tr>
                  <th className='corner-header'>
                    <span style={{ color: '#a6c10b', fontSize: 10, textTransform: 'uppercase' }}>
                      {eixosInvertidos ? 'Eixo Y: Produtos' : 'Eixo Y: Geografia'}
                    </span>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2 }}>{labelLinhas}</div>
                  </th>
                  {colunas.map(col => {
                    const statsCol = eixosInvertidos
                      ? gridData.statsGeografias[col.id]
                      : gridData.statsProdutos[col.id]
                    const hitRate = statsCol?.hitRate || 0

                    return (
                      <th key={col.id} className='col-header' title={col.nome}>
                        <span className='col-title'>{col.nome}</span>
                        <span className='col-hit-rate'>{hitRate}% hit</span>
                      </th>
                    )
                  })}
                  <th className='summary-header'>
                    <div className='col-title'>% Hit Rate (Linha)</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {linhas.map(row => {
                  const statsLinha = eixosInvertidos
                    ? gridData.statsProdutos[row.id]
                    : gridData.statsGeografias[row.id]
                  const hitRate = statsLinha?.hitRate || 0

                  return (
                    <tr key={row.id}>
                      <td className='sticky-row-header' title={row.nome}>
                        <div>{row.nome}</div>
                        {(row as any).extra && <div className='row-meta'>{(row as any).extra}</div>}
                      </td>

                      {colunas.map(col => {
                        const gId = eixosInvertidos ? col.id : row.id
                        const pId = eixosInvertidos ? row.id : col.id
                        const key = `${gId}__${pId}`
                        const cell = gridData.cells[key]

                        return (
                          <td
                            key={col.id}
                            className='cell-slot'
                            onMouseEnter={e => handleMouseEnterCell(e, row, col)}
                            onMouseLeave={handleMouseLeaveCell}
                          >
                            {cell?.hasPositivacao ? (
                              <div className='cell-hit'>
                                <FaCheck size={9} />
                                <span>{cell.pdvsPositivados}</span>
                              </div>
                            ) : (
                              <span className='cell-empty'>—</span>
                            )}
                          </td>
                        )
                      })}

                      <td className='sticky-row-summary'>
                        <div>{hitRate}%</div>
                        <div className='sub-stat'>
                          {statsLinha?.hits || 0}/{colunas.length}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>

              <tfoot>
                <tr>
                  <td className='corner-footer'>
                    <span>% COBERTURA DA COLUNA</span>
                  </td>
                  {colunas.map(col => {
                    const statsCol = eixosInvertidos
                      ? gridData.statsGeografias[col.id]
                      : gridData.statsProdutos[col.id]
                    const hitRate = statsCol?.hitRate || 0

                    return <td key={col.id}>{hitRate}%</td>
                  })}
                  <td className='summary-footer'>{gridData.totais.hitRateGeral}% Geral</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </NavalGridContainer>

      {/* 4. TOOLTIP FLUTUANTE CUSTOMIZADO NO HOVER */}
      {tooltipInfo && (
        <div
          style={{
            position: 'fixed',
            left: tooltipInfo.x,
            top: tooltipInfo.y,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999,
            pointerEvents: 'none',
            background: isLight ? '#0f172a' : '#1e293b',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            minWidth: 220,
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 13, color: '#a6c10b', marginBottom: 2 }}>
            {tooltipInfo.prodNome}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
            Praça: {tooltipInfo.geoNome}
          </div>

          {tooltipInfo.hasPositivacao ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#cbd5e1' }}>PDVs Positivados:</span>
                <strong style={{ color: '#10b981' }}>{tooltipInfo.pdvs} PDV(s)</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#cbd5e1' }}>Faturamento:</span>
                <strong>
                  {tooltipInfo.faturamento.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#cbd5e1' }}>Volume:</span>
                <strong>
                  {tooltipInfo.caixas.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} cx
                </strong>
              </div>
            </div>
          ) : (
            <div style={{ color: '#ef4444', fontStyle: 'italic', fontSize: 11 }}>
              Água (Sem Positivação no Período)
            </div>
          )}

          <div
            style={{
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              marginTop: 8,
              paddingTop: 6,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10,
              color: '#94a3b8',
            }}
          >
            <span>Hit Produto: {tooltipInfo.hitRateProd}%</span>
            <span>Hit Praça: {tooltipInfo.hitRateGeo}%</span>
          </div>
        </div>
      )}
    </NavalGridWrapper>
  )
}
